import React from 'react';
import { Box, Drawer, AppBar, Toolbar, Typography, IconButton, useTheme, useMediaQuery, Avatar, Menu, MenuItem, Chip, Badge } from '@mui/material';
import { Menu as MenuIcon, AccountCircle, Notifications, AdminPanelSettings, ExitToApp } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { toggleSidebar, setMobileView } from '../../store/slices/uiSlice';
import { logoutUser } from '../../store/slices/authSlice';
import Sidebar from './Sidebar';
import EmergencyAlert from '../Emergency/EmergencyAlert';
import { BRAND } from '../../constants/branding';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sidebarOpen } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState<null | HTMLElement>(null);

  // Check if user has admin privileges
  const isAdmin = user?.is_superuser || 
                  user?.roles?.includes('admin') || 
                  user?.roles?.includes('superuser');

  React.useEffect(() => {
    dispatch(setMobileView(isMobile));
  }, [isMobile, dispatch]);

  const handleDrawerToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await dispatch(logoutUser());
    navigate('/login', { replace: true });
  };

  const handleAdminPanelClick = () => {
    navigate('/admin');
    handleProfileMenuClose();
  };

  const drawerWidth = 280;

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
          ml: { md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: '0.04em' }}>
            {BRAND.nameUpper}
            {isAdmin && (
              <Chip 
                label="Admin Mode" 
                size="small" 
                color="secondary" 
                sx={{ ml: 2, color: 'white', fontWeight: 'bold' }} 
              />
            )}
          </Typography>
          
          {/* Notifications */}
          <IconButton 
            color="inherit" 
            onClick={handleNotificationMenuOpen}
          >
            <Badge badgeContent={3} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          
          {/* Admin Panel Button (only for admins) */}
          {isAdmin && (
            <IconButton 
              color="inherit" 
              onClick={handleAdminPanelClick}
              title="Admin Panel"
            >
              <AdminPanelSettings />
            </IconButton>
          )}
          
          {/* Profile Menu */}
          <IconButton 
            color="inherit" 
            onClick={handleProfileMenuOpen}
            sx={{ ml: 1 }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: 'secondary.main',
                fontSize: '1rem'
              }}
            >
              {(user?.full_name || user?.username)?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          
          <Typography variant="body2" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
            {user?.full_name || user?.username || 'User'}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" noWrap>
            {user?.full_name || user?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
          {user?.subscription && (
            <Box sx={{ mt: 0.5 }}>
              <Chip 
                label={user.subscription.plan} 
                size="small" 
                color={user.subscription.plan === 'enterprise' ? 'success' : 
                       user.subscription.plan === 'pro' ? 'primary' : 'default'}
              />
            </Box>
          )}
        </Box>
        
        {isAdmin && (
          <MenuItem onClick={handleAdminPanelClick}>
            <AdminPanelSettings sx={{ mr: 1 }} />
            Admin Panel
          </MenuItem>
        )}
        
        <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
          <AccountCircle sx={{ mr: 1 }} />
          Profile Settings
        </MenuItem>
        
        <MenuItem onClick={handleLogout}>
          <ExitToApp sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        PaperProps={{
          sx: { mt: 1, minWidth: 300, maxHeight: 400 }
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Notifications</Typography>
        </Box>
        
        <MenuItem onClick={handleNotificationMenuClose}>
          <Box>
            <Typography variant="body2">Post scheduled successfully</Typography>
            <Typography variant="caption" color="text.secondary">2 minutes ago</Typography>
          </Box>
        </MenuItem>
        
        <MenuItem onClick={handleNotificationMenuClose}>
          <Box>
            <Typography variant="body2">Analytics report ready</Typography>
            <Typography variant="caption" color="text.secondary">1 hour ago</Typography>
          </Box>
        </MenuItem>
        
        <MenuItem onClick={handleNotificationMenuClose}>
          <Box>
            <Typography variant="body2">New trending topic available</Typography>
            <Typography variant="caption" color="text.secondary">3 hours ago</Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={sidebarOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          <Sidebar />
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
          mt: '64px', // AppBar height
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Emergency Alert */}
        <EmergencyAlert dismissible={true} />
        
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 